import { getPrettyPhone } from "@whatslly/commons/utils";
import { getChannelTypeToText, type MessageChannelType, ReportedMessage } from "@whatslly/types/instant-messaging/messagesEmitter";
import { i18n } from "./i18nService";

type SenderAndReceiverFields = Partial<
  Pick<ReportedMessage, "isSentByMe" | "isGroupMsg" | "notifyName" | "authorPhone" | "senderName" | "senderPhone" | "receiverName" | "receiverPhone">
> & { chatType?: string; channelType?: string };

const getGroupSenderAndReceiverName = (message: SenderAndReceiverFields) => {
  const isSentByMe = String(message.isSentByMe) === "true";
  const groupName = isSentByMe ? message.receiverName : message.senderName;
  return {
    sender: {
      name: message.notifyName,
      phone: getPrettyPhone(message.authorPhone ?? ""),
      sourcePhone: getPrettyPhone(message.authorPhone ?? ""),
      sourceName: message.notifyName,
    },
    receiver: {
      name: groupName,
      phone: undefined,
      sourcePhone: undefined,
      sourceName: groupName,
    },
  };
};

const getNewsLetterSenderAndReceiverName = (message: SenderAndReceiverFields) => {
  const isSentByMe = String(message.isSentByMe) === "true";
  return {
    sender: {
      name: message.senderName,
      phone: isSentByMe ? getPrettyPhone(message.senderPhone ?? "") : undefined,
      sourcePhone: isSentByMe ? getPrettyPhone(message.senderPhone ?? "") : undefined,
      sourceName: message.senderName,
    },
    receiver: {
      name: message.receiverName,
      phone: isSentByMe ? undefined : getPrettyPhone(message.receiverPhone ?? ""),
      sourcePhone: isSentByMe ? undefined : getPrettyPhone(message.receiverPhone ?? ""),
      sourceName: message.receiverName,
    },
  };
};

const getStatusSenderAndReceiverName = (message: SenderAndReceiverFields) => {
  const channelName = getChannelTypeToText((message.channelType as MessageChannelType) || "whatsAppWeb");
  return {
    sender: {
      name: message.senderName,
      phone: getPrettyPhone(message.senderPhone ?? ""),
      sourcePhone: getPrettyPhone(message.senderPhone ?? ""),
      sourceName: message.senderName,
    },
    receiver: {
      name: `${channelName} ${i18n.t("status")}`,
      phone: getPrettyPhone(message.receiverPhone ?? ""),
      sourcePhone: getPrettyPhone(message.receiverPhone ?? ""),
      sourceName: message.receiverName,
    },
  };
};

export const getReportedMessageSenderAndReceiver = (message: SenderAndReceiverFields) => {
  const isGroupMessage = String(message.isGroupMsg) === "true";
  if (isGroupMessage) return getGroupSenderAndReceiverName(message);
  const isNewsLetter = message.chatType === "newsletter";
  if (isNewsLetter) return getNewsLetterSenderAndReceiverName(message);
  const isStatus = message.chatType === "status";
  if (isStatus) return getStatusSenderAndReceiverName(message);
  const names = {
    senderName: message.senderName || getPrettyPhone(message.senderPhone ?? ""),
    receiverName: message.receiverName || getPrettyPhone(message.receiverPhone ?? ""),
  };
  const phones = {
    senderPhone: String(names.senderName) !== String(getPrettyPhone(message.senderPhone ?? "")) && getPrettyPhone(message.senderPhone ?? ""),
    receiverPhone: String(names.receiverName) !== String(getPrettyPhone(message.receiverPhone ?? "")) && getPrettyPhone(message.receiverPhone ?? ""),
  };

  return {
    sender: {
      name: names.senderName,
      phone: phones.senderPhone,
      sourcePhone: getPrettyPhone(message.senderPhone ?? ""),
      sourceName: message.senderName,
    },
    receiver: {
      name: names.receiverName,
      phone: phones.receiverPhone,
      sourcePhone: getPrettyPhone(message.receiverPhone ?? ""),
      sourceName: message.receiverName,
    },
  };
};
